import { useParams } from "react-router-dom";
import { useEffect } from "react";
import styled from "styled-components";
import { useQuery } from "@apollo/client";
import useAuth from "../../hooks/useAuth";
import GET_RECOMMENDATION from "../../graphql/getRecommendation";
import getPriorities from "../../graphql/getPriorities";
import ScreenLoader from "../../components/ScreenLoader/ScreenLoader";
import Heading from "../../components/Heading/Heading";
import RecommendationInfo from "../../components/RecommendationInfo/RecommendationInfo";
import {useHistory, useLocation} from 'react-router-dom'
import OperationsIcon from "../../components/RecommendationTabs/icons/OperationsIcon";
import PerformanceIcon from "../../components/RecommendationTabs/icons/PerformanceIcon";
import CostIcon from "../../components/RecommendationTabs/icons/CostIcon";
import ReliabilityIcon from "../../components/RecommendationTabs/icons/ReliabilityIcon";
import SecurityIcon from "../../components/RecommendationTabs/icons/SecurityIcon";
import TrainingIcon from "../../components/RecommendationTabs/icons/TrainingIcon";
import SustainabilityIcon from "../../components/RecommendationTabs/icons/SustainabilityIcon";

const StyledHeading = styled(Heading)`
  margin-bottom: 30px;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 45px;
  margin-bottom: 44px;
  color: #4b5882;
  font-size: 14px;

  @media only screen and (max-width: 920px) {
    padding: 0 18px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  grid-gap: 30px;
  margin-bottom: 50px;

  *:nth-child(2) {
    grid-column: 1 / 2;
  }

  & > *:last-child {
    grid-row: 1 / 2;
    grid-column: 2 / 3;
  }

  @media only screen and (max-width: 1000px) {
    grid-template-columns: 1fr;

    & > *:last-child {
      grid-row: revert;
      grid-column: revert;
      max-width: revert;
    }
  }
`;

const IconBox = styled.div`
  height: 40px;
  width: 40px;
  background-color: #2f5993;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-right: 16px;

  svg {
    height: 20px;
    fill: white;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const DescriptionWrapper = styled.div`
  background-color: #fff;
  border-radius: 10px;
  border: 1px solid #a5acc2;
  padding: 10px;
  min-height: 100px;
  hyphens: auto;
  line-height: 1.4;
`;

const ErrorMsg = styled.p`
  color: red;
`;

const RecommendationDetail = () => {
  const recid = useParams();
  const history = useHistory();
  const location = useLocation()

  const { selectedOrg, orgOptions, setOrg } = useAuth();
  const { loading, error, data } = useQuery(GET_RECOMMENDATION, {
    variables: { id: recid.id },
    context: { version: 1 }
  });

  const { loading: prioritiesLoading, error: prioritiesError, data: prioritiesData, } = useQuery(getPriorities, {
    variables: { organizationId: selectedOrg.value },
    context: { version: 4 },
  });

  useEffect(() => {
    if (!data?.getRecommendation?.organizationId || !orgOptions) return; //checks if data exists so dont fail

    const targetOrg = orgOptions.find( //gets org of recommendation
      org => org.value.split(":")[1] === data.getRecommendation.organizationId
    );

    if (targetOrg) {//this is a security check if target org is not in your org options it will be undefined
      // Set organization if it's not already selected
      if (selectedOrg?.value !== targetOrg.value) {
        setOrg(targetOrg);
      }
    } else {
      // Redirect if the user doesn't have access to the org
      const basePath = location.pathname.split("/recommendations/")[0];
      history.push(`${basePath}/recommendations/security`);
    }
  }, [data, orgOptions, selectedOrg, location.pathname, history, setOrg]);

  if (loading || prioritiesLoading) return <ScreenLoader />;
  if (error) return <ErrorMsg>{error.message}</ErrorMsg>;
  if (prioritiesError) return <ErrorMsg>{prioritiesError.message}</ErrorMsg>;

  if (!data?.getRecommendation) {
    const basePath = location.pathname.split("/recommendations/")[0];
    history.push(`${basePath}/recommendations/security`);
    return null;
  }

  const priorities = prioritiesData?.listIssues?.items || [];
  let recommendationId = data?.getRecommendation?.id || "";
  let MovedtoTask = false;
  if (recommendationId.startsWith("rec:")) {
    recommendationId = recommendationId.slice(4); // Remove "rec:"
  }
  let matchingIssueId = null;
  
  // Find the issue with the recid
  for (const issue of priorities) {
    let originalLink = issue.originalLink || "";

    if (originalLink.startsWith("rec:")) {
      originalLink = originalLink.slice(4); // Remove "rec:"
    }
    
    if (originalLink === recommendationId) {
      matchingIssueId = issue.id;
      MovedtoTask = true;
      break;
    }
  }

  function getIcon(pillar) {
    if (!pillar) return null;
  
    switch (pillar.toLowerCase()) {
      case 'cost':
        return <CostIcon />;
      case 'reliability':
        return <ReliabilityIcon />;
      case 'performance':
        return <PerformanceIcon />;
      case 'training':
        return <TrainingIcon />;
      case 'operations':
        return <OperationsIcon />;
      case 'security':
        return <SecurityIcon />;
      case 'sustainability':
        return <SustainabilityIcon />;
      default:
        return <SecurityIcon />;
    }
  }

  return (
    <Box>
      <StyledHeading>
        <TitleWrapper>
          <IconBox>{getIcon(data.getRecommendation.awsPillar)}</IconBox>
          {data.getRecommendation.title}
        </TitleWrapper>
      </StyledHeading>
      <Grid>
        {data.getRecommendation.description && (
          <DescriptionWrapper>
            <p style={{ whiteSpace: "pre-line" }}>
              {data.getRecommendation.description}
            </p>
          </DescriptionWrapper>
        )}
        <RecommendationInfo
          recId={data.getRecommendation.id}
          status={
            (() => {
              const status = data.getRecommendation.status;
              if (MovedtoTask === true && matchingIssueId != null) {
                return (
                  <a
                  href={`/priority-board/${matchingIssueId}`}
                  rel="noopener noreferrer"
                  >
                  Moved to Team Support Board
                  </a>
                );
              } else {
                return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
              }
            })() // Immediately invoke the function
          }
          organization={selectedOrg.label}
          Pillar={data.getRecommendation.awsPillar}
        />
      </Grid>
    </Box>
  );
};

export default RecommendationDetail;
